// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.

import {
  PARENT_HEADER_HEIGHT,
  PARENT_WIDTH,
  WIDTH_DECREMENT,
  CHILD_HEIGHT,
  CHILD_WIDTH,
  CHILD_SPACING,
  GAP,
  HANDLE_HEIGHT,
  GROUP_NODE_HEIGHT,
  CANVAS_CELL_W,
  CANVAS_CELL_H,
} from '../constants/constants';
import { getEquipmentGroupHandles, getEquipmentHandles } from '../connections/connections';
import { resolveClosedGroupHeight } from '../layoutCore/layoutCore';
import { toggleNode } from '../toggle/toggle';
import { sortGroupsByHierarchy } from '../normalization/normalization';

const SUBGROUP_MIN_WIDTH = 210; // minimum width for deeply nested subgroups

const HIDDEN_NODE_UUIDS = new Set(['36f0754c-8013-4d36-ae10-a8c23dcb2bd7']); // Preset Manager

/* ================= FACILITY NODE (EQUIPMENT GROUP AT TOP LEVEL) ================= */
export const createFacilityNode = ({ group }) => ({
  id: group.equip_group_uuid,
  type: 'facility',
  position: { x: 0, y: 0 },
  draggable: false,
  selectable: false,
  hidden: true,
  data: {
    state: group,
    isFacility: true,
  },
});

/* ================= EQUIPMENT GROUP NODE ================= */
export const createGroupNode = ({
  group,
  index,
  prevNode,
  facilityEquipmentGroupUuid,
  equipmentList,
  groupsList,
  activeNodeId,
  openState = {},
  layouts = {},
}) => {
  const isChild = Boolean(group.parent_uuid) && group.parent_uuid !== facilityEquipmentGroupUuid;

  // Build map for recursion
  const groupsMap = new Map(groupsList.map(g => [g.equip_group_uuid, g]));

  // Current group width (recursive)
  const resolvedWidth = resolveGroupWidth({
    group,
    groupsMap,
    facilityEquipmentGroupUuid,
  });

  // Resolve parent width if subgroup
  let parentWidth = PARENT_WIDTH;

  if (isChild) {
    const parentGroup = groupsMap.get(group.parent_uuid);

    if (parentGroup) {
      parentWidth = resolveGroupWidth({
        group: parentGroup,
        groupsMap,
        facilityEquipmentGroupUuid,
      });
    }
  }

  // Child: centered within parent, stacked by index (placeholder until layoutChildren runs).
  // Root: { x: 0, y: 0 } placeholder — rebuildNodesFromGroups sets the real position.
  const position = isChild
    ? {
        x: Math.floor((parentWidth - resolvedWidth) / 2),
        y: GROUP_NODE_HEIGHT + CHILD_SPACING + index * (CHILD_HEIGHT + CHILD_SPACING),
      }
    : { x: 0, y: 0 };

  const groupHandles = getEquipmentGroupHandles(
    group,
    equipmentList,
    groupsList,
    facilityEquipmentGroupUuid
  );
  const handles = {
    inputs: groupHandles.filter(h => h.connectionType === 'input'),
    outputs: groupHandles.filter(h => h.connectionType === 'output'),
  };

  return {
    id: group.equip_group_uuid,
    type: 'equipmentGroup',
    position,

    draggable: !isChild,
    ...(isChild && {
      parentId: group.parent_uuid,
      extent: 'parent',
      hidden: prevNode?.hidden ?? true,
    }),

    data: {
      subGroup: isChild,
      state: group,
      width: resolvedWidth,
      height: resolveClosedGroupHeight(handles),
      isOpen: openState[group.equip_group_uuid] ?? false,
      layout: layouts[group.equip_group_uuid] || 'vertical',
      baseWidth: resolvedWidth,
      isActive: group.equip_group_uuid === activeNodeId,
      hasError: prevNode?.data?.hasError ?? false,
      isHighlighted: prevNode?.data?.isHighlighted ?? false,
      handles,
    },
  };
};
/* ================= EQUIPMENT NODE ================= */
export const createEquipmentNode = ({
  equip,
  index,
  groupId,
  parentWidth,
  prevNode,
  equipmentList,
  groupsList,
  activeNodeId,
}) => {
  const isChild = Boolean(groupId);

  const centerX = isChild && parentWidth ? Math.floor((parentWidth - CHILD_WIDTH) / 2) : 25;

  // Child: stacked within parent (placeholder until layoutChildren runs).
  // Root: { x: 0, y: 0 } placeholder — rebuildNodesFromGroups sets the real position.
  const position = isChild
    ? {
        x: centerX,
        y: PARENT_HEADER_HEIGHT + CHILD_SPACING + index * (CHILD_HEIGHT + CHILD_SPACING),
      }
    : { x: 0, y: 0 };

  // Handle rendering
  const rawHandles = getEquipmentHandles(equip, equipmentList, groupsList);

  const inputs = rawHandles.filter(h => h.connectionType === 'input');

  const outputs = rawHandles.filter(h => h.connectionType === 'output');

  // Calculate height
  const totalHandles = inputs.length + outputs.length;
  const rowCount = Math.max(totalHandles, 1);

  const BASE_OFFSET = 60; // header area
  const calculatedHeight = BASE_OFFSET + rowCount * HANDLE_HEIGHT + GAP;

  return {
    id: equip.co_equip_uuid,
    type: 'equipment',
    position,

    draggable: !isChild,

    ...(isChild && {
      parentId: groupId,
      extent: 'parent',
      hidden: prevNode?.parentId === groupId ? (prevNode?.hidden ?? true) : true,
    }),

    data: {
      state: equip,
      width: CHILD_WIDTH,
      height: prevNode?.data?.height ?? calculatedHeight,
      hasError: prevNode?.data?.hasError ?? false,
      isActive: equip.co_equip_uuid === activeNodeId,
      isHighlighted: prevNode?.data?.isHighlighted ?? false,
      handles: {
        inputs,
        outputs,
      },
    },
  };
};

/* =========== RECURSIVE NODES BUILDER FROM EQUIPMENT GROUP RESPONSE ============== */
const buildNodes = ({
  groups = [],
  prevMap,
  facilityEquipmentGroupUuid,
  equipmentList,
  activeNodeId,
  openState = {},
  layouts = {},
}) => {
  if (!groups.length) {
    return [];
  }

  // Helper to remove duplicates based on key
  const removeDuplicates = (items, key) => [
    ...new Map(items.map(item => [item[key], item])).values(),
  ];

  // Separate facility group from other equipment groups
  const facilityGroup = groups.find(g => g?.equip_group_uuid === facilityEquipmentGroupUuid);

  const equipmentGroups = groups.filter(g => g?.equip_group_uuid !== facilityEquipmentGroupUuid);

  // Facility node and its equipment(standalone equipment)
  const facilityNodes = facilityGroup
    ? [
        createFacilityNode({ group: facilityGroup }),
        ...removeDuplicates(
          facilityGroup.equipment?.filter(e => e.direct_member) ?? [],
          'co_equip_uuid'
        ).map((equip, i) =>
          createEquipmentNode({
            equip,
            index: i,
            groupId: null,
            prevNode: prevMap.get(equip.co_equip_uuid),
            equipmentList,
            groupsList: groups,
            activeNodeId,
          })
        ),
      ]
    : [];

  // Equipment group nodes and their equipment(nested equipment inside groups)
  const groupNodes = equipmentGroups.flatMap((group, index) => {
    const groupNode = createGroupNode({
      group,
      index,
      prevNode: prevMap.get(group.equip_group_uuid),
      facilityEquipmentGroupUuid,
      equipmentList,
      groupsList: groups,
      activeNodeId,
      openState,
      layouts,
    });

    const equipmentNodes = removeDuplicates(
      group.equipment?.filter(e => e.direct_member) ?? [],
      'co_equip_uuid'
    ).map((equip, i) =>
      createEquipmentNode({
        equip,
        index: i,
        groupId: group.equip_group_uuid,
        parentWidth: groupNode.data.width,
        prevNode: prevMap.get(equip.co_equip_uuid),
        equipmentList,
        groupsList: groups,
        activeNodeId,
      })
    );

    return [groupNode, ...equipmentNodes];
  });

  return [...facilityNodes, ...groupNodes];
};

// Builds React Flow nodes from buildNodes.
// Returns { nodes, clearedPositionIds } — clearedPositionIds are nodes whose parent changed
// (e.g. moved to facility root); the caller should evict their stale saved positions.
//
// @param {Array}  groups                    — normalised equipment groups from the backend
// @param {Array}  prevNodes                 — current React Flow nodes; [] on initial mount,
//                                             live nodes on backend update
// @param {Object} savedPositions            — { [nodeId]: {x,y} } persisted positions from
//                                             Redux; used as fallback for nodes with no prevNode
//                                             (new nodes or first render after page refresh)
// @param {string} facilityEquipmentGroupUuid — UUID of the top-level facility group; used to
//                                             distinguish root groups from subgroups
// @param {Array}  equipmentList             — full company equipment list; used to build
//                                             connection handles on each node
// @param {string} activeNodeId              — currently selected node; sets isActive on its node
// @param {Object} openState                 — { [nodeId]: boolean } persisted open/closed state
// @param {Object} layouts                   — { [nodeId]: 'vertical'|'horizontal'|'grid' }
// @param {Object} viewport                  — React Flow viewport {x, y, zoom}; places new nodes near screen centre
export const rebuildNodesFromGroups = ({
  groups = [],
  prevNodes = [],
  savedPositions = {},
  facilityEquipmentGroupUuid,
  equipmentList,
  activeNodeId,
  openState = {},
  layouts = {},
  viewport = null,
}) => {
  const prevMap = new Map(prevNodes.map(n => [n.id, n]));
  const openIds = prevNodes
    .filter(n => n.type === 'equipmentGroup' && n.data?.isOpen && !n.hidden)
    .map(n => n.id);

  const built = buildNodes({
    groups: sortGroupsByHierarchy(groups),
    prevMap,
    facilityEquipmentGroupUuid,
    equipmentList,
    activeNodeId,
    openState,
    layouts,
  });

  const clearedPositionIds = [];

  // Collect positions of existing top-level nodes (groups and standalone equipment) so findEmptyPosition knows which cells are taken.
  const isTopLevelNode = ({ parentId, type }) =>
    !parentId && (type === 'equipmentGroup' || type === 'equipment');

  const occupiedPositions = built
    .filter(
      ({ parentId, type, id }) =>
        isTopLevelNode({ parentId, type }) && (prevMap.has(id) || savedPositions[id])
    )
    .map(({ id }) => {
      const node = prevMap.get(id);
      const pos = node?.position ?? savedPositions[id];
      const w = node?.data?.width;
      const h = node?.data?.height;
      return w && h ? { ...pos, w, h } : pos;
    });

  const withPositions = built.map(node => {
    const prevNode = prevMap.get(node.id);

    if (!prevNode) {
      node.data.isHidden = HIDDEN_NODE_UUIDS.has(node.id);
      const saved = savedPositions[node.id];
      if (saved) {
        node.position = saved;
      } else if (isTopLevelNode(node)) {
        // New top-level node: place near screen centre, avoiding existing nodes
        node.position = findEmptyPosition(occupiedPositions, viewport);
      }
      return node;
    }

    const movedChildToRoot = prevNode.parentId && !node.parentId;

    if (movedChildToRoot) {
      clearedPositionIds.push(node.id);
      return { ...node, parentId: undefined, extent: undefined, position: node.position };
    }

    const position =
      prevNode.parentId !== node.parentId ? node.position : (prevNode.position ?? node.position);

    node.data.isHidden = HIDDEN_NODE_UUIDS.has(node.id);
    return { ...prevNode, ...node, position };
  });

  const nodes = openIds.reduce((acc, id) => toggleNode(acc, id, true), withPositions);
  return { nodes, clearedPositionIds };
};

/* ============================================================
   ============ NEW NODE POSITION BASED ON VIEWPORT ===========
   ============================================================ */
// Uses the React Flow viewport to find a free spot near the screen centre for a new node.
// Pushes the chosen position into occupiedPositions so two new nodes never land on the same spot.
export const findEmptyPosition = (occupiedPositions, viewport = null) => {
  const cellW = CANVAS_CELL_W;
  const cellH = CANVAS_CELL_H;
  const fallbackWidth = 1280; // fallback to standard desktop width if window.innerWidth is unavailable
  const fallbackHeight = 800; // fallback to standard desktop height if window.innerHeight is unavailable

  const { x: vpX = 0, y: vpY = 0, zoom = 1 } = viewport ?? {};
  const containerW = typeof window !== 'undefined' ? window.innerWidth : fallbackWidth;
  const containerH = typeof window !== 'undefined' ? window.innerHeight : fallbackHeight;
  const centerX = (-vpX + containerW / 2) / zoom;
  const centerY = (-vpY + containerH / 2) / zoom;

  const centerGx = Math.round(centerX / cellW);
  const centerGy = Math.round(centerY / cellH);

  // Mark every grid cell each node covers — open nodes can span multiple cells vertically
  const occupied = new Set();
  occupiedPositions.forEach(({ x, y, w, h }) => {
    const startGx = Math.floor(x / cellW);
    const endGx = Math.floor((x + (w ?? cellW) - 1) / cellW);
    const startGy = Math.floor(y / cellH);
    const endGy = Math.floor((y + (h ?? cellH) - 1) / cellH);
    for (let gx = startGx; gx <= endGx; gx++) {
      for (let gy = startGy; gy <= endGy; gy++) {
        occupied.add(`${gx},${gy}`);
      }
    }
  });

  // Check cells in rings outward from centre until a free one is found
  for (let radius = 0; radius <= 50; radius++) {
    for (let dx = -radius; dx <= radius; dx++) {
      for (let dy = -radius; dy <= radius; dy++) {
        if (Math.abs(dx) !== radius && Math.abs(dy) !== radius) {
          continue;
        }
        const key = `${centerGx + dx},${centerGy + dy}`;
        if (!occupied.has(key)) {
          const pos = { x: (centerGx + dx) * cellW, y: (centerGy + dy) * cellH };
          occupiedPositions.push(pos);
          return pos;
        }
      }
    }
  }

  return { x: centerX, y: centerY };
};

/* ============================================================
   ================= EQUIPMENT GROUP WIDTH ===================
   ============================================================ */

export const resolveGroupWidth = ({
  group,
  groupsMap,
  facilityEquipmentGroupUuid,
  visited = new Set(),
}) => {
  if (!group?.equip_group_uuid) {
    return PARENT_WIDTH;
  }

  const id = group.equip_group_uuid;

  //  prevent circular recursion (websocket race on updateEquipmentGroup parent_uuid)
  if (visited.has(id)) {
    return SUBGROUP_MIN_WIDTH;
  }

  visited.add(id);

  // facility/root stop condition
  if (!group.parent_uuid || group.equip_group_uuid === facilityEquipmentGroupUuid) {
    return PARENT_WIDTH;
  }

  const parent = groupsMap.get(group.parent_uuid);

  if (!parent) {
    return PARENT_WIDTH;
  }

  const parentWidth = resolveGroupWidth({
    group: parent,
    groupsMap,
    facilityEquipmentGroupUuid,
    visited,
  });

  const calculatedWidth = parentWidth - WIDTH_DECREMENT;

  return Math.max(calculatedWidth, SUBGROUP_MIN_WIDTH);
};
